import React, { useEffect, useRef } from "react";

import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { v4 as uuidv4 } from "uuid";

import useMediaColorByFormat from "@/src/hooks/useMediaColorByFormat";

import MediaIcon from "./MediaIcon";
import { DRAG_FORMAT_UNKNOWN, DRAG_TYPE } from "../../../constants/dragTypes";
import { TIMESLOT } from "../../../constants/media";
import { getDurationString } from "../../../utils/data/Content";

import type { JSX } from "react";

import type { MediaItem } from "./MediaLibraryItemList";
import type { MediaLibraryDragItem } from "../../../types/fast";

export const ITEM_HEIGHT = 72;
interface CollectedDragProps {
  opacity: number;
  cursor: string;
}

interface MediaLibraryItemProps {
  media: MediaItem;
  isCustomDragLayer?: boolean;
  isDraggingDisabled?: boolean;
}

function MediaLibraryItem({
  media,
  isCustomDragLayer,
  isDraggingDisabled,
}: MediaLibraryItemProps): JSX.Element {
  const mediaFormat =
    media.format && typeof media.format !== "string"
      ? media.format.key
      : media.format;

  const getMediaColor = useMediaColorByFormat();

  const mediaColor =
    media.type === TIMESLOT
      ? (media.color ?? getMediaColor())
      : getMediaColor(mediaFormat);

  const dragRef = useRef<HTMLDivElement>(null);

  const [{ opacity, cursor }, dragConnector, preview] = useDrag<
    MediaLibraryDragItem,
    unknown,
    CollectedDragProps
  >(
    () => ({
      type: `${DRAG_TYPE.MEDIA_LIBRARY_ITEM}-${mediaFormat ?? DRAG_FORMAT_UNKNOWN}`,
      item: () => ({
        label: media.label,
        mediaId: media.uuid,
        duration: media.source_duration,
        formatKey: mediaFormat,
        type: media.type,
        uuid: uuidv4(),
        color: mediaColor,
        cuepoints: media.cuepoints,
        poster: media.object_picture,
      }),
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
        cursor: monitor.isDragging() ? "grabbing" : "grab",
      }),
    }),
    [],
  );

  dragConnector(dragRef);

  const prettyDuration = getDurationString({
    duration: media.source_duration / 1000,
    isShort: true,
  });

  useEffect(() => {
    if (isCustomDragLayer) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [preview, isCustomDragLayer]);

  return (
    <div
      key={media.uuid}
      className="flex flex-row rounded-[0.15625rem] pb-[0.46875rem] drop-shadow"
      style={{
        height: `${ITEM_HEIGHT}px`,
        opacity,
      }}
    >
      <div
        ref={isDraggingDisabled ? null : dragRef}
        className="bg-card relative flex w-full flex-row items-center justify-between gap-[0.46875rem] rounded-r-lg py-[0.9375rem] pr-[0.78125rem] pl-[1.25rem]"
        style={{
          cursor: isDraggingDisabled ? "not-allowed" : cursor,
        }}
      >
        {/* This div is necessary for the use of the Shift key when dragging (https://issues.chromium.org/issues/41469770) */}
        <div className="pointer-events-none w-full">
          <div
            className="absolute top-0 left-0 h-full w-[0.78125rem]"
            style={{
              backgroundColor: mediaColor,
            }}
          />
          <div className="flex gap-[0.46875rem] overflow-hidden">
            <MediaIcon
              hasCuepoints={Boolean(media.cuepoints?.length)}
              mediaType={media.type}
            />
            <div className="flex flex-col overflow-hidden">
              <span className="truncate font-semibold">{media.label}</span>
              <span>{prettyDuration}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MediaLibraryItem;
